/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
  NavigationGuardNext
} from "vue-router";
import { I18n } from "vue-i18n";
import Home from "../views/Home.vue";
import { applyHead, RouteMeta } from "../composables/useHead";
import { LOCALES, localePath, Locale } from "../utils/locale";

const routeMeta: Record<string, RouteMeta> = {
  home: {
    title: {
      fr: "Madarion | Ingénierie logicielle, cloud native et IA agentique",
      en: "Madarion | Software engineering, cloud native and agentic AI"
    },
    description: {
      fr:
        "Madarion conçoit, livre et exploite des applications d'entreprise, des plateformes Kubernetes et cloud native, des API gateways et des agents IA, pour les éditeurs de logiciels.",
      en:
        "Madarion builds, ships and runs enterprise applications, Kubernetes and cloud-native platforms, API gateways and AI agents, for software companies."
    },
    ogImage: "https://www.madarion.com/og-home.png"
  },
  connectera: {
    title: {
      fr:
        "Connectera — L'IA agentique pour les processus d'entreprise | Madarion",
      en: "Connectera — Agentic AI for enterprise processes | Madarion"
    },
    description: {
      fr:
        "Des agents IA qui font tourner vos processus : ils agissent dans vos systèmes via leurs API, suivent vos procédures, et vos équipes gardent les décisions.",
      en:
        "AI agents that run your processes: they act in your systems through their APIs, follow your procedures, and your people keep the decisions."
    },
    ogImage: "https://www.madarion.com/og-connectera.png",
    // Factual properties only: no ratings, reviews or prices that do not exist.
    jsonLd: (lang) => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Connectera",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `https://www.madarion.com${localePath("/connectera", lang)}`,
      inLanguage: lang,
      description:
        lang === "fr"
          ? "IA agentique pour les processus d'entreprise : des agents qui agissent dans vos systèmes via leurs API, selon vos règles ; hébergés en Europe ou dans votre compte AWS."
          : "Agentic AI for enterprise processes: agents that act in your systems through their APIs, by your rules; hosted in Europe or in your own AWS account.",
      publisher: {
        "@type": "Organization",
        name: "Madarion",
        url: "https://www.madarion.com"
      }
    })
  },
  about: {
    title: { fr: "À propos | Madarion", en: "About | Madarion" },
    description: {
      fr:
        "Madarion conçoit, construit et exploite des plateformes cloud, et édite Connectera.",
      en:
        "Madarion designs, builds and runs cloud platforms, and makes Connectera."
    },
    ogImage: "https://www.madarion.com/og-home.png"
  },
  contact: {
    title: { fr: "Contact | Madarion", en: "Contact | Madarion" },
    description: {
      fr: "Écrivez à Madarion pour un projet cloud ou une démo de Connectera.",
      en: "Write to Madarion about a cloud project or a Connectera demo."
    },
    ogImage: "https://www.madarion.com/og-home.png"
  },
  security: {
    title: {
      fr: "Sécurité et données — Connectera | Madarion",
      en: "Security and data \u2014 Connectera | Madarion"
    },
    description: {
      fr:
        "Où vivent les données de Connectera, qui peut y accéder, et comment elles sont protégées.",
      en:
        "Where Connectera's data lives, who can reach it, and how it is protected."
    },
    ogImage: "https://www.madarion.com/og-home.png"
  },
  legal: {
    title: { fr: "Mentions légales | Madarion", en: "Legal notice | Madarion" },
    description: {
      fr: "Éditeur du site madarion.com.",
      en: "Publisher of madarion.com."
    },
    ogImage: "https://www.madarion.com/og-home.png"
  },
  privacy: {
    title: {
      fr: "Politique de confidentialité | Madarion",
      en: "Privacy policy | Madarion"
    },
    description: {
      fr:
        "Quelles données personnelles Madarion collecte, pourquoi, et vos droits.",
      en: "What personal data Madarion collects, why, and your rights."
    },
    ogImage: "https://www.madarion.com/og-home.png"
  },
  notFound: {
    title: {
      fr: "Page introuvable | Madarion",
      en: "Page not found | Madarion"
    },
    description: {
      fr:
        "Cette page n'existe pas. Retrouvez Madarion et Connectera depuis l'accueil.",
      en:
        "This page does not exist. Find Madarion and Connectera from the home page."
    }
  }
};

// Every page exists once per language: French at its path, English under /en (utils/locale.ts).
const pages: {
  path: string;
  name: string;
  headKey: string;
  component: RouteRecordRaw["component"];
}[] = [
  { path: "/", name: "Home", headKey: "home", component: Home },
  {
    path: "/connectera",
    name: "Connectera",
    headKey: "connectera",
    component: () => import("../views/Connectera.vue")
  },
  {
    path: "/about",
    name: "About",
    headKey: "about",
    component: () => import("../views/About.vue")
  },
  {
    path: "/contact",
    name: "Contact",
    headKey: "contact",
    component: () => import("../views/Contact.vue")
  },
  {
    path: "/security",
    name: "Security",
    headKey: "security",
    component: () => import("../views/Security.vue")
  },
  {
    path: "/legal-notice",
    name: "LegalNotice",
    headKey: "legal",
    component: () => import("../views/LegalNotice.vue")
  },
  {
    path: "/privacy",
    name: "Privacy",
    headKey: "privacy",
    component: () => import("../views/Privacy.vue")
  }
];

const setupRouter = function (i18n: I18n<any, any, any, any>) {
  const routes: Array<RouteRecordRaw> = [
    ...LOCALES.flatMap((lang) =>
      pages.map(
        (p): RouteRecordRaw => ({
          path: localePath(p.path, lang),
          name: `${p.name}-${lang}`,
          component: p.component as any,
          meta: { headKey: p.headKey, locale: lang, basePath: p.path }
        })
      )
    ),
    // Anything else is a real 404 page (noindex), not the home page under a stray URL.
    {
      path: "/en/:pathMatch(.*)*",
      name: "NotFound-en",
      component: () => import("../views/NotFound.vue"),
      meta: { headKey: "notFound", locale: "en", noindex: true }
    },
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound-fr",
      component: () => import("../views/NotFound.vue"),
      meta: { headKey: "notFound", locale: "fr", noindex: true }
    }
  ];

  const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from) {
      // A page change fades out first (App.vue, out-in), so wait for the new page before scrolling to a hash.
      const wait = to.path !== from.path ? 260 : 0;
      return new Promise((resolve) =>
        setTimeout(
          () => resolve(to.hash ? { el: to.hash, top: 64 } : { top: 0 }),
          wait
        )
      );
    }
  });

  router.beforeEach(
    (
      to: RouteLocationNormalized,
      _from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      // The URL decides the language; the switcher changes the URL, never the other way round.
      const lang = (to.meta.locale as Locale) ?? "fr";
      const loc = i18n.global.locale as any;
      if (typeof loc === "object") loc.value = lang;
      else (i18n.global as any).locale = lang;

      const key = (to.meta.headKey as string) ?? "home";
      applyHead(routeMeta[key], lang, (to.meta.basePath as string) ?? to.path, {
        noindex: !!to.meta.noindex
      });
      next();
    }
  );

  return router;
};

export default setupRouter;
